<script setup lang="ts">
import { computed, h, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { NLayout, NLayoutHeader, NLayoutContent, NMenu, NIcon, NButton, NDrawer, NDrawerContent } from 'naive-ui';
import type { MenuOption } from 'naive-ui';
import { CallOutline, RecordingOutline, MicOutline, ListOutline, MenuOutline, StatsChartOutline } from '@vicons/ionicons5';
import AlertBanner from '@/components/AlertBanner.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const hideNav = computed(() => route.meta.hideNav === true);
const showDrawer = ref(false);
const isMobile = ref(window.innerWidth < 768);

function onResize() {
  isMobile.value = window.innerWidth < 768;
}

onMounted(() => window.addEventListener('resize', onResize));
onUnmounted(() => window.removeEventListener('resize', onResize));

const menuOptions = computed<MenuOption[]>(() => [
  { label: t('cc-call-queues'), key: '/call-center', icon: () => h(NIcon, null, { default: () => h(CallOutline) }) },
  { label: t('voicemail-title'), key: '/voicemail/new', icon: () => h(NIcon, null, { default: () => h(RecordingOutline) }) },
  { label: t('nav-greetings'), key: '/greetings', icon: () => h(NIcon, null, { default: () => h(MicOutline) }) },
  { label: t('answer-rules-title'), key: '/answer-rules', icon: () => h(NIcon, null, { default: () => h(ListOutline) }) },
  { label: t('nav-reports', 'Reports'), key: 'reports-external', icon: () => h(NIcon, null, { default: () => h(StatsChartOutline) }) },
]);

const activeKey = computed(() => {
  const path = route.path;
  if (path.startsWith('/voicemail')) return '/voicemail/new';
  return path;
});

function handleMenuUpdate(key: string) {
  if (key === 'reports-external') {
    window.open('https://exclient.ca/portal/stats/queuestats/queue/', '_blank');
    return;
  }
  router.push(key);
  showDrawer.value = false;
}
</script>

<template>
  <NLayout style="height: 100vh;">
    <NLayoutHeader v-if="!hideNav" bordered class="app-header">
      <AlertBanner />
      <!-- Desktop: horizontal menu with labels -->
      <NMenu
        v-if="!isMobile"
        mode="horizontal"
        :options="menuOptions"
        :value="activeKey"
        @update:value="handleMenuUpdate"
      />
      <!-- Mobile: compact icon bar -->
      <div v-else class="mobile-nav">
        <NButton quaternary size="medium" @click="showDrawer = true">
          <template #icon><NIcon :component="MenuOutline" :size="24" /></template>
        </NButton>
        <div class="mobile-nav-icons">
          <NButton
            v-for="opt in menuOptions"
            :key="opt.key as string"
            quaternary
            size="large"
            :type="activeKey === opt.key ? 'primary' : 'default'"
            @click="handleMenuUpdate(opt.key as string)"
            style="min-width: 44px; min-height: 44px;"
          >
            <template #icon>
              <NIcon :size="24">
                <CallOutline v-if="opt.key === '/call-center'" />
                <RecordingOutline v-else-if="opt.key === '/voicemail/new'" />
                <MicOutline v-else-if="opt.key === '/greetings'" />
                <ListOutline v-else-if="opt.key === '/answer-rules'" />
              </NIcon>
            </template>
          </NButton>
        </div>
      </div>
    </NLayoutHeader>
    <AlertBanner v-if="hideNav" />

    <!-- Mobile drawer with full menu labels -->
    <NDrawer v-model:show="showDrawer" placement="left" :width="250">
      <NDrawerContent title="CallEase">
        <NMenu
          :options="menuOptions"
          :value="activeKey"
          @update:value="handleMenuUpdate"
        />
      </NDrawerContent>
    </NDrawer>

    <NLayoutContent
      :content-style="hideNav
        ? 'height: 100vh; overflow: auto;'
        : 'height: calc(100vh - 42px); overflow: auto;'"
    >
      <router-view />
    </NLayoutContent>
  </NLayout>
</template>

<style scoped>
.app-header {
  padding: 0;
}

.mobile-nav {
  display: flex;
  align-items: center;
  padding: 4px 8px;
}

.mobile-nav-icons {
  display: flex;
  align-items: center;
  margin-left: auto;
  gap: 0;
}
</style>
